import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Code, CheckCircle, TrendingUp, Users, Zap, GitBranch } from 'lucide-react';

const SoftwareEngineers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I quantify my impact as a software engineer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Track metrics like lines of code reduced (technical debt), API response time improvements (e.g., '40% faster'), bug reduction rates, deployment frequency increases, and user-facing features shipped. Use numbers: 'Reduced page load time from 3.2s to 1.1s, improving user retention by 15%.'"
        }
      },
      {
        "@type": "Question",
        "name": "What achievements should software engineers highlight in performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Focus on: system reliability improvements, successful feature launches, code review contributions, mentoring activities, incident resolution, architecture decisions, and cross-team collaboration. Quantify everything possible with metrics like uptime percentages, sprint velocity, or deployment success rates."
        }
      },
      {
        "@type": "Question",
        "name": "How often should software engineers document their wins?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document weekly or after each sprint. Waiting until review time leads to forgotten accomplishments. Track every PR merged, bug fixed, feature shipped, and colleague helped. Use a brag document tool like BragBox to capture wins in real-time."
        }
      },
      {
        "@type": "Question",
        "name": "Should I mention code reviews in my performance review?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Code reviews demonstrate technical leadership, mentorship, and quality standards. Track the number of reviews completed, patterns you've caught, and knowledge you've shared. Example: 'Reviewed 45 PRs this quarter, catching 12 critical bugs before production.'"
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for Software Engineers | BragBox</title>
        <meta name="description" content="Ace your software engineering performance review with actionable tips. Learn how to quantify code impact, document technical achievements, and showcase your engineering contributions." />
        <meta name="keywords" content="software engineer performance review, developer self-assessment, engineering achievements, code metrics, tech career growth" />
        <link rel="canonical" href="https://bragbox.ai/tips/software-engineers" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <Code className="w-4 h-4" />
              Software Engineering Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for Software Engineers
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Stop underselling your technical contributions. Learn how to document code impact, quantify engineering achievements, and present your work in a way that resonates with both technical and non-technical stakeholders.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why Software Engineers Struggle with Self-Reviews</h2>
            <p className="text-gray-300 mb-6">
              You've shipped features, squashed bugs, and kept systems running at 99.9% uptime. But when review season hits, you stare at a blank document wondering how to explain that refactoring a 2,000-line monolith into microservices was actually a big deal.
            </p>
            <p className="text-gray-300">
              The problem isn't your work. It's that engineering impact is often invisible. Your manager sees the deployed feature, not the 47 edge cases you handled. They know the system stayed up, not that you caught a race condition at 2 AM that would have taken down production.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your Engineering Review</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Quantify Performance Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Don't just say "improved API performance." Be specific: "Optimized database queries reducing average response time from 450ms to 120ms, supporting 3x more concurrent users without additional infrastructure costs."
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Implemented Redis caching layer that reduced database load by 60% and cut P95 latency from 800ms to 95ms, saving an estimated $2,400/month in server costs."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <GitBranch className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Track Technical Debt Reduction</h3>
                    <p className="text-gray-300 mb-3">
                      Refactoring rarely gets the credit it deserves. Frame it in business terms: reduced onboarding time for new developers, fewer production incidents, or faster feature delivery.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Refactored authentication module from 3,200 lines to 800 lines, reducing related bug reports by 75% and cutting new developer ramp-up time from 2 weeks to 3 days."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Document Incident Response</h3>
                    <p className="text-gray-300 mb-3">
                      Every on-call shift where you kept systems running is an achievement. Track incidents resolved, MTTR improvements, and preventive measures you implemented.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Led incident response for critical payment system outage, restoring service in 12 minutes (vs. 45-minute average). Implemented circuit breaker pattern preventing 3 similar incidents this quarter."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Highlight Code Review Contributions</h3>
                    <p className="text-gray-300 mb-3">
                      Code reviews demonstrate technical leadership and quality standards. Track reviews completed, patterns you've established, and knowledge shared with the team.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Completed 127 code reviews this quarter, identifying 23 security vulnerabilities before production. Created review checklist adopted by entire team, reducing post-deploy bugs by 40%."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Connect Features to Business Outcomes</h3>
                    <p className="text-gray-300 mb-3">
                      Shipping code is great. Shipping code that moved the needle is better. Link your technical work to user metrics, revenue impact, or strategic company goals.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Built real-time notification system that increased user engagement by 28%. Feature drove 15% uplift in paid conversions, generating estimated $180K ARR."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Document Mentorship and Knowledge Sharing</h3>
                    <p className="text-gray-300 mb-3">
                      Senior engineers are expected to multiply team output. Track documentation written, engineers mentored, tech talks given, and processes you've improved.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Mentored 2 junior engineers through promotion cycles. Created 15-page architecture documentation reducing onboarding questions by 80%. Led 4 internal tech talks on GraphQL best practices."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Track Cross-Team Collaboration</h3>
                    <p className="text-gray-300 mb-3">
                      Working effectively across teams is a leadership skill. Document APIs you built for other teams, integrations you supported, and cross-functional projects you contributed to.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Designed and implemented shared authentication service used by 4 product teams, eliminating 3 months of duplicated work. Led weekly cross-team sync that reduced integration issues by 60%."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Capture Testing and Quality Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Test coverage and CI/CD improvements prevent future fires. These unglamorous wins deserve recognition.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Increased test coverage from 45% to 89%. Implemented automated E2E testing that catches regressions before deploy, reducing production bugs by 65% and QA cycle time from 3 days to 4 hours."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Show Initiative Beyond Assigned Work</h3>
                    <p className="text-gray-300 mb-3">
                      Did you identify and fix a problem nobody asked you to? Did you propose and implement an improvement? Self-driven work shows ownership and leadership potential.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Identified memory leak causing gradual performance degradation. Proposed and implemented fix during hack week, preventing estimated $50K in emergency infrastructure scaling."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Keep a Running Brag Document</h3>
                    <p className="text-gray-300 mb-3">
                      The best time to document an achievement is right after it happens. Don't rely on memory when review season comes. Use a tool like BragBox to capture wins in real-time.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">After every PR merged, sprint completed, or incident resolved, take 2 minutes to log the win. Include what you did, the impact, and any metrics. Future you will thank present you.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics Software Engineers Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Code Quality</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Test coverage percentage</li>
                  <li>• Bug escape rate to production</li>
                  <li>• Code review turnaround time</li>
                  <li>• Technical debt reduction</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Performance</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• API response times (P50, P95, P99)</li>
                  <li>• Page load speed improvements</li>
                  <li>• System uptime percentage</li>
                  <li>• Resource utilization optimization</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Delivery</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Sprint velocity/story points</li>
                  <li>• Features shipped per quarter</li>
                  <li>• Deployment frequency</li>
                  <li>• Lead time for changes</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Impact</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• User engagement metrics</li>
                  <li>• Revenue attributed to features</li>
                  <li>• Cost savings from optimizations</li>
                  <li>• Customer satisfaction improvements</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do I quantify my impact as a software engineer?</h3>
                <p className="text-gray-300">Track metrics like lines of code reduced (technical debt), API response time improvements (e.g., "40% faster"), bug reduction rates, deployment frequency increases, and user-facing features shipped. Use numbers: "Reduced page load time from 3.2s to 1.1s, improving user retention by 15%."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What achievements should software engineers highlight in performance reviews?</h3>
                <p className="text-gray-300">Focus on: system reliability improvements, successful feature launches, code review contributions, mentoring activities, incident resolution, architecture decisions, and cross-team collaboration. Quantify everything possible with metrics like uptime percentages, sprint velocity, or deployment success rates.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How often should software engineers document their wins?</h3>
                <p className="text-gray-300">Document weekly or after each sprint. Waiting until review time leads to forgotten accomplishments. Track every PR merged, bug fixed, feature shipped, and colleague helped. Use a brag document tool like BragBox to capture wins in real-time.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">Should I mention code reviews in my performance review?</h3>
                <p className="text-gray-300">Absolutely. Code reviews demonstrate technical leadership, mentorship, and quality standards. Track the number of reviews completed, patterns you've caught, and knowledge you've shared. Example: "Reviewed 45 PRs this quarter, catching 12 critical bugs before production."</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Stop Forgetting Your Wins</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps software engineers track achievements in real-time, so you're always ready for your next performance review.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SoftwareEngineers;
